import React, { useEffect, useState } from 'react';
import { Box, Button, Container, Divider, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { errorMessages } from '~/constants/errorMessages.ts';
import GuaranteesApplicationsTable from './components/applications/ApplicationsTable.tsx';
import { guaranteesClientPageStyles } from './styles.ts';
import {
  GUARANTEES_APPLICATIONS_DEFAULT_ROWS_PER_PAGE,
  guaranteesClientText,
} from './constants.ts';
import { commonStyles, dataFormStyles } from '~/modules/guarantees/legacy/styles.ts';
import {
  buildGuaranteesClientApplicationPathByStatus,
  buildGuaranteesApplicationPath,
  getGuaranteesErrorMessage,
  isGuaranteesAdministratorPath,
} from '~/modules/guarantees/utils.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import {
  selectGuaranteesApplications,
  selectGuaranteesApplicationsError,
  selectGuaranteesApplicationsPageConfiguration,
  selectGuaranteesApplicationsStatus,
  selectGuaranteesCreateApplicationStatus,
} from '~/store/guarantees/selectors.ts';
import {
  createGuaranteesApplication,
  fetchGuaranteesApplications,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import { GuaranteesFetchStatus, type TGuaranteesFlowType } from '~/store/guarantees/types.ts';
import type { AppDispatch } from '~/store/store.ts';
import type { TGuaranteesApplicationRow } from './types.ts';

const GuaranteesClientPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: contragentId = '' } = useParams();
  const [applicationsPage, setApplicationsPage] = useState(0);
  const [applicationsRowsPerPage, setApplicationsRowsPerPage] = useState(
    GUARANTEES_APPLICATIONS_DEFAULT_ROWS_PER_PAGE
  );
  const [modalAlertText, setModalAlertText] = useState('');
  const applications = useSelector(selectGuaranteesApplications);
  const applicationsError = useSelector(selectGuaranteesApplicationsError);
  const applicationsPageConfiguration = useSelector(selectGuaranteesApplicationsPageConfiguration);
  const applicationsStatus = useSelector(selectGuaranteesApplicationsStatus);
  const createApplicationStatus = useSelector(selectGuaranteesCreateApplicationStatus);
  const isApplicationsLoading = applicationsStatus === GuaranteesFetchStatus.Loading;
  const isCreateApplicationLoading = createApplicationStatus === GuaranteesFetchStatus.Loading;

  useEffect(() => {
    if (!contragentId) {
      return;
    }

    dispatch(
      fetchGuaranteesApplications({
        contragentId,
        page: applicationsPage,
        size: applicationsRowsPerPage,
      })
    );
  }, [applicationsPage, applicationsRowsPerPage, contragentId, dispatch]);

  const handleCloseAlertModal = () => setModalAlertText('');
  const isAdministratorFlow = isGuaranteesAdministratorPath(location.pathname);
  const getStartedPath = isAdministratorFlow
    ? '/guarantees/administrator/get-started'
    : '/guarantees/get-started';

  useEffect(() => {
    if (applicationsError) {
      setModalAlertText(getGuaranteesErrorMessage(applicationsError));
    }
  }, [applicationsError]);

  const handleCreateApplication = async () => {
    if (!contragentId || isCreateApplicationLoading) {
      return;
    }

    const flowType: TGuaranteesFlowType = isAdministratorFlow ? 'MANUAL' : 'WEB';

    try {
      const createdApplication = await dispatch(
        createGuaranteesApplication({
          contragentId,
          flowType,
        })
      ).unwrap();

      if (!createdApplication.id) {
        setModalAlertText(errorMessages.SERVICE_UNAVAILABLE);
        return;
      }

      navigate(
        buildGuaranteesApplicationPath(createdApplication.id, contragentId, isAdministratorFlow)
      );
    } catch (error) {
      setModalAlertText(getGuaranteesErrorMessage(error));
    }
  };

  const handleOpenApplication = (application: TGuaranteesApplicationRow) => {
    const applicationPath = isAdministratorFlow
      ? buildGuaranteesApplicationPath(application.id, contragentId, true)
      : buildGuaranteesClientApplicationPathByStatus(
          application.id,
          contragentId,
          application.status
        );

    navigate(applicationPath);
  };

  return (
    <Box sx={guaranteesClientPageStyles.pageRoot}>
      <Container sx={guaranteesClientPageStyles.pageContainer}>
        <Box sx={commonStyles.header}>
          <Box>
            <Typography variant="h4" sx={guaranteesClientPageStyles.pageTitle}>
              {guaranteesClientText.pageTitle}
            </Typography>
            <Typography variant="subtitle2" sx={guaranteesClientPageStyles.pageSubtitle}>
              {guaranteesClientText.contragentIdLabel}: {contragentId}
            </Typography>
          </Box>

          <Box sx={guaranteesClientPageStyles.headerActions}>
            <Button
              variant="contained"
              onClick={handleCreateApplication}
              disabled={isCreateApplicationLoading}
              startIcon={isCreateApplicationLoading ? <ButtonLoadingIcon /> : undefined}
              sx={dataFormStyles.documentPrimaryAction}
            >
              {guaranteesClientText.createApplicationButton}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate(getStartedPath)}
              sx={commonStyles.backButton}
            >
              {guaranteesClientText.backButton}
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={commonStyles.content}>
          <GuaranteesApplicationsTable
            applications={applications}
            isLoading={isApplicationsLoading}
            page={applicationsPage}
            pageConfiguration={applicationsPageConfiguration}
            rowsPerPage={applicationsRowsPerPage}
            onPageChange={setApplicationsPage}
            onOpenApplication={handleOpenApplication}
            onRowsPerPageChange={(nextRowsPerPage) => {
              setApplicationsRowsPerPage(nextRowsPerPage);
              setApplicationsPage(0);
            }}
          />

          {!!modalAlertText && (
            <ModalAlert
              isOpen={!!modalAlertText}
              text={modalAlertText}
              onClose={handleCloseAlertModal}
            />
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default GuaranteesClientPage;
